import type { Availability, HistoryPoint, RenderMode, SparkPoint } from '@/api/types';

/**
 * §5.1 и §5.2 спеки. Единственное место в проекте, где решается,
 * рисовать ли линию и где её рвать.
 *
 * ВАЖНО (Global Constraint 5): сервер присылает render_mode сам.
 * Компоненты обязаны использовать серверное значение; renderModeFor — страховка
 * для мест, где режим не пришёл (например, спарклайн из stay.spark).
 * Тест series.contract.test.ts доказывает, что оба правила совпадают.
 */

export interface SeriesPoint {
  t: number; // epoch ms
  price: number;
  cached?: boolean;
  /** Разрыв перед этой точкой, объявленный сервером (HistoryPoint.gap_before). */
  gapBefore?: boolean;
  availability?: Availability;
  sourceId?: string;
}

/** Пропуск больше ДВУХ ожидаемых интервалов — видимый разрыв. */
export const GAP_INTERVAL_FACTOR = 2;

export function renderModeFor(count: number): RenderMode {
  if (count >= 3) return 'line';
  if (count === 2) return 'points';
  if (count === 1) return 'point';
  return 'none';
}

/** §5.1: при одном замере процент изменения не существует. */
export function canShowPercent(mode: RenderMode): boolean {
  return mode === 'points' || mode === 'line';
}

export function shortSeriesNote(mode: RenderMode): string | null {
  if (mode === 'none' || mode === 'point') return 'истории пока нет';
  if (mode === 'points') return 'данных мало';
  return null;
}

export function toSeriesPoints(points: HistoryPoint[]): SeriesPoint[] {
  return points.map((point) => ({
    t: Date.parse(point.observed_at),
    price: point.total,
    cached: point.is_cached,
    gapBefore: point.gap_before,
    availability: point.availability,
    sourceId: point.source_id,
  }));
}

/** Спарклайн в списке поездок: у SparkPoint только время и сумма. */
export function sparkToSeriesPoints(points: SparkPoint[]): SeriesPoint[] {
  return points.map((point) => ({ t: Date.parse(point.observed_at), price: point.total }));
}

export function splitSegments(
  points: SeriesPoint[],
  expectedIntervalSec: number,
): SeriesPoint[][] {
  if (points.length === 0) return [];

  const sorted = [...points].sort((a, b) => a.t - b.t);
  const maxGapMs = expectedIntervalSec * 1000 * GAP_INTERVAL_FACTOR;

  const segments: SeriesPoint[][] = [];
  let current: SeriesPoint[] = [sorted[0]!];

  for (let i = 1; i < sorted.length; i += 1) {
    const point = sorted[i]!;
    const previous = sorted[i - 1]!;
    // Приоритет у серверного флага: сервер видит всю историю, клиент — окно.
    const declaredGap = point.gapBefore === true;
    const measuredGap = point.t - previous.t > maxGapMs;

    if (declaredGap || measuredGap) {
      segments.push(current);
      current = [point];
    } else {
      current.push(point);
    }
  }
  segments.push(current);
  return segments;
}

export function gapsBetween(
  segments: SeriesPoint[][],
): Array<{ fromT: number; toT: number; ms: number }> {
  const gaps: Array<{ fromT: number; toT: number; ms: number }> = [];
  for (let i = 1; i < segments.length; i += 1) {
    const prev = segments[i - 1]!;
    const next = segments[i]!;
    const fromT = prev[prev.length - 1]!.t;
    const toT = next[0]!.t;
    gaps.push({ fromT, toT, ms: toT - fromT });
  }
  return gaps;
}
